/**
 * Copilot API Client
 * Provides access to the Copilot REST API for conversation management
 * Follows the same patterns as the GekkoVet API client
 */

import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { getAWSConfig } from '../aws.config';
import {
  ConversationListResponse,
  ConversationDetails,
  CopilotApiError as CopilotApiErrorInterface
} from '../types/conversations';
import type {
  DocumentSummary,
  Document,
  DocumentVersionInfo,
  UserWritingTemplate,
  UserWritingTemplateInput,
  GenerateTemplateContentRequest,
} from '../types/documents';
import type {
  CreateReportRequest,
  CreateReportResponse,
  GetUploadUrlRequest,
  GetUploadUrlResponse,
  ConfirmUploadResponse,
  FinalizeReportResponse,
  GetReportResponse,
} from '../types/insurance';
import type {
  ChatCostsResponse,
  InsuranceCostsResponse,
  CostSummaryResponse,
  PricingSyncResponse,
} from '../types/admin';

export class CopilotApiError extends Error {
  statusCode: number;
  response?: CopilotApiErrorInterface | Error | unknown;

  constructor(message: string, statusCode: number, response?: CopilotApiErrorInterface | Error | unknown) {
    super(message);
    this.statusCode = statusCode;
    this.response = response;
    this.name = 'CopilotApiError';
  }
}

export class CopilotApiClient {
  private baseUrl: string;

  constructor() {
    const config = getAWSConfig();
    if (!config.restApiEndpoint) {
      throw new Error('REST_API_ENDPOINT not configured');
    }
    this.baseUrl = config.restApiEndpoint.replace(/\/$/, '');
  }

  /**
   * Get authentication token from AWS Cognito session
   */
  private async getAuthToken(): Promise<string> {
    try {
      // Check if user is authenticated
      await getCurrentUser();
      
      // Get the current session
      const session = await fetchAuthSession();
      // Use ID token for API Gateway Cognito authorizer
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        throw new Error('No ID token available');
      }

      console.log('Using ID token for API authentication');
      return token;
    } catch (error) {
      console.error('Failed to get auth token:', error);
      throw new CopilotApiError('Authentication failed', 401, error);
    }
  }

  /**
   * Make authenticated request to the Copilot API
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    try {
      const token = await this.getAuthToken();
      
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers,
        },
      });

      if (!response.ok) {
        let errorData: CopilotApiErrorInterface;
        try {
          errorData = await response.json();
        } catch {
          // If error response is not JSON, create a generic error
          errorData = {
            error: 'HTTP_ERROR',
            message: response.statusText || `HTTP ${response.status}`,
            statusCode: response.status,
          };
        }
        
        throw new CopilotApiError(
          errorData.message || `HTTP ${response.status}`,
          response.status,
          errorData
        );
      }

      const data: T = await response.json();
      return data;
    } catch (error) {
      if (error instanceof CopilotApiError) {
        throw error;
      }
      
      // Handle network errors or other issues
      console.error('Copilot API request failed:', error);
      throw new CopilotApiError(
        'Network request failed', 
        0, 
        error
      );
    }
  }

  /**
   * Get list of conversations for the authenticated user
   */
  async getConversations(params?: {
    limit?: number;
    nextToken?: string;
  }): Promise<ConversationListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.nextToken) searchParams.set('nextToken', params.nextToken);
    
    const query = searchParams.toString();
    const endpoint = `/conversations${query ? `?${query}` : ''}`;
    
    return this.request<ConversationListResponse>(endpoint);
  }

  /**
   * Get detailed conversation with messages
   */
  async getConversation(conversationId: string): Promise<ConversationDetails> {
    return this.request<ConversationDetails>(`/conversations/${conversationId}`);
  }

  /**
   * Search conversations by query string
   */
  async searchConversations(query: string, params?: {
    limit?: number;
    nextToken?: string;
  }): Promise<ConversationListResponse> {
    const searchParams = new URLSearchParams({ q: query });
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    if (params?.nextToken) searchParams.set('nextToken', params.nextToken);
    
    return this.request<ConversationListResponse>(`/conversations/search?${searchParams}`);
  }

  /**
   * Generic GET method for extensibility
   */
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  /**
   * Generic POST method for extensibility
   */
  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // Document API methods

  /**
   * Get documents for a conversation
   */
  async getDocumentsForConversation(conversationId: string): Promise<DocumentSummary[]> {
    try {
      const response = await this.request<{ documents: DocumentSummary[] }>(`/conversations/${conversationId}/documents`);
      return response.documents;
    } catch (error) {
      // Return empty array if endpoint not yet available
      console.warn('Documents API not available:', error);
      return [];
    }
  }

  /**
   * Get a specific document by ID with optional version
   */
  async getDocument(documentId: string, version?: number): Promise<Document | null> {
    try {
      const versionParam = version ? `?version=${version}` : '';
      const response = await this.request<{ document: Document }>(`/documents/${documentId}${versionParam}`);
      return response.document;
    } catch (error) {
      console.warn('Document API not available:', error);
      return null;
    }
  }

  /**
   * Get version history for a document
   */
  async getDocumentVersions(documentId: string): Promise<DocumentVersionInfo[]> {
    try {
      const response = await this.request<{ versions: DocumentVersionInfo[] }>(`/documents/${documentId}/versions`);
      return response.versions;
    } catch (error) {
      console.warn('Document versions API not available:', error);
      return [];
    }
  }

  // Writing Templates API methods

  /**
   * Get all writing templates for the authenticated user
   */
  async getWritingTemplates(): Promise<UserWritingTemplate[]> {
    const response = await this.request<{ templates: UserWritingTemplate[] }>('/writing-templates');
    return response.templates;
  }

  /**
   * Create a new writing template
   */
  async createWritingTemplate(input: UserWritingTemplateInput): Promise<UserWritingTemplate> {
    const response = await this.request<{ template: UserWritingTemplate }>('/writing-templates', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return response.template;
  }

  /**
   * Update an existing writing template
   */
  async updateWritingTemplate(
    templateId: string,
    input: Partial<UserWritingTemplateInput>
  ): Promise<UserWritingTemplate> {
    const response = await this.request<{ template: UserWritingTemplate }>(
      `/writing-templates/${templateId}`,
      {
        method: 'PUT',
        body: JSON.stringify(input),
      }
    );
    return response.template;
  }

  /**
   * Delete a writing template
   */
  async deleteWritingTemplate(templateId: string): Promise<void> {
    await this.request<{ success: boolean }>(`/writing-templates/${templateId}`, {
      method: 'DELETE',
    });
  }

  /**
   * Generate template content using LLM
   */
  async generateTemplateContent(request: GenerateTemplateContentRequest): Promise<string> {
    const response = await this.request<{ content: string }>('/writing-templates/generate', {
      method: 'POST',
      body: JSON.stringify(request),
    });
    return response.content;
  }

  // Insurance Reports API methods

  /**
   * Create a new insurance report
   */
  async createInsuranceReport(data: CreateReportRequest): Promise<CreateReportResponse> {
    return this.request<CreateReportResponse>('/insurance-summaries', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Request a presigned S3 upload URL for a PDF
   */
  async getInsuranceReportUploadUrl(
    reportId: string,
    data: GetUploadUrlRequest
  ): Promise<GetUploadUrlResponse> {
    return this.request<GetUploadUrlResponse>(
      `/insurance-summaries/${reportId}/pdfs/upload-url`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Upload raw PDF bytes directly to S3 using presigned URL.
   * This does NOT go through the API — it's a direct S3 PUT.
   */
  async uploadPDFToS3(uploadUrl: string, file: File): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/pdf',
      },
      body: file,
    });

    if (!response.ok) {
      throw new CopilotApiError(
        `S3 upload failed: ${response.status} ${response.statusText}`,
        response.status
      );
    }
  }

  /**
   * Confirm that a PDF has been uploaded to S3 and trigger processing
   */
  async confirmInsuranceReportPDFUpload(
    reportId: string,
    pdfId: string
  ): Promise<ConfirmUploadResponse> {
    return this.request<ConfirmUploadResponse>(
      `/insurance-summaries/${reportId}/pdfs/${pdfId}/uploaded`,
      {
        method: 'POST',
        body: JSON.stringify({}),
      }
    );
  }

  /**
   * Finalize an insurance report (signal "no more PDFs to upload")
   */
  async finalizeInsuranceReport(reportId: string): Promise<FinalizeReportResponse> {
    return this.request<FinalizeReportResponse>(
      `/insurance-summaries/${reportId}/finalize`,
      {
        method: 'POST',
        body: JSON.stringify({}),
      }
    );
  }

  /**
   * Get insurance report status and results (for polling)
   */
  async getInsuranceReport(reportId: string): Promise<GetReportResponse> {
    return this.request<GetReportResponse>(`/insurance-summaries/${reportId}`);
  }

  // Admin API methods

  /**
   * Get chat costs for admin dashboard
   */
  async getAdminChatCosts(params?: {
    from?: string;
    to?: string;
    limit?: number;
  }): Promise<ChatCostsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    const query = searchParams.toString();
    return this.request<ChatCostsResponse>(`/admin/costs/chat${query ? `?${query}` : ''}`);
  }

  /**
   * Get insurance costs for admin dashboard
   */
  async getAdminInsuranceCosts(params?: {
    from?: string;
    to?: string;
    limit?: number;
  }): Promise<InsuranceCostsResponse> {
    const searchParams = new URLSearchParams();
    if (params?.from) searchParams.set('from', params.from);
    if (params?.to) searchParams.set('to', params.to);
    if (params?.limit) searchParams.set('limit', params.limit.toString());
    const query = searchParams.toString();
    return this.request<InsuranceCostsResponse>(`/admin/costs/insurance${query ? `?${query}` : ''}`);
  }

  /**
   * Get cost summary for admin dashboard
   */
  async getAdminCostSummary(): Promise<CostSummaryResponse> {
    return this.request<CostSummaryResponse>('/admin/costs/summary');
  }

  /**
   * Trigger pricing sync from AWS Price List API
   */
  async syncAdminPricing(): Promise<PricingSyncResponse> {
    return this.request<PricingSyncResponse>('/admin/costs/sync-pricing', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }
}

// Singleton instance
export const copilotApi = new CopilotApiClient();
